import { FileQuestion, Play, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../components/Common/Button';
import CommonDialog from '../components/Common/Dialog';
import PracticeTestLayout from '../components/Layout/PracticeTestLayout';
import Pagination from '../components/ReUsableComponents/CMS/Pagination';
import {
  setInitialQuestionsState,
  setIsStartTest,
  setUserTestId
} from '../redux/Slices/QuestionsSlice';
import { setCompleteTestData } from '../redux/Slices/StartTestSlice/completeTestData';
import {
  ExamPattern,
  Round,
  setNewTemplete
} from '../redux/Slices/StartTestSlice/newTemplete';
import {
  setCurrentTime,
  setHasReadRoundInsructions,
  setInitailStartFullTestState,
  setUserCompanyTestsStatus,
  setUserSelectedCompanyTest
} from '../redux/Slices/StartTestSlice/startFulTest';
import { useAppDispatch, useAppSelector } from '../redux/store/hooks';
import {
  useGetStudentScheduleTestsQuery,
  useStartStudentScheduleTestMutation
} from '../services/scheduleTestApi';
import { useLazyGetWrittenTestFullDetailsQuery } from '../services/startTestAPI/resultsTestApi';
import {
  useDiscardTestMutation,
  useLazyGetInstructionsForTestQuery,
  useLazyGetTestsForCompanyQuery,
  useLazyResumeTestQuery
} from '../services/startTestAPI/startTestApi';

export type AssignedTest = {
  id: string;
  isActive: boolean;
  createdDate: string;
  updatedDate: string;
  testStartTime: string;
  testEndTime: string;
  testId: string;
  createdBy: string;
  tenantId: string;
  companyId: string;
  tagId: string;
  testName: string;
  userTestId: string | null;
  userId: string;
  testStatus: 'NOT_YET' | 'STARTED' | 'COMPLETED';
  canStart: boolean;
};

export type AssignedTestsResponse = {
  data: AssignedTest[];
  pagesize: 0;
  totalpages: 0;
  totaltests: 0;
};

const AssignedTests = () => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const { scheduleStudentTestsData } = useAppSelector(
    state => state.root.scheduleTest
  );
  const userId = useAppSelector(state => state.root.auth.userId);
  const { isStartTest } = useAppSelector(state => state.root.questions);
  useGetStudentScheduleTestsQuery(
    { userId, page, size },
    { refetchOnMountOrArgChange: true }
  );

  if (isStartTest) {
    return <PracticeTestLayout isScheduleTest={true} />;
  }

  return (
    <div className='flex h-full flex-col justify-between space-y-1.5 p-6'>
      <div className='flex flex-col space-y-1.5 overflow-y-scroll p-6'>
        {scheduleStudentTestsData?.data?.length > 0 ? (
          scheduleStudentTestsData?.data?.map((test: AssignedTest) => (
            <TestCard key={test?.id} test={test} />
          ))
        ) : (
          <div className='flex min-h-full flex-col items-center justify-center bg-gray-50 p-4'>
            <div className='max-w-md text-center'>
              <div className='mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full bg-blue-100'>
                <FileQuestion className='h-10 w-10 text-blue-600' />
              </div>

              <h2 className='mb-3 text-2xl font-semibold text-gray-800'>
                No Tests Available
              </h2>

              <p className='mb-2 text-lg text-gray-600'>
                There are no tests assigned to you right now.
              </p>

              <p className='text-gray-500'>
                Please check back later for new Tests.
              </p>

              <div className='mt-8'>
                <button
                  onClick={() => window.location.reload()}
                  className='rounded-lg bg-blue-600 px-6 py-2 text-white transition-colors hover:bg-blue-700'
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {scheduleStudentTestsData?.totaltests !== 0 && (
        <Pagination
          pageSize={size}
          totalquestions={scheduleStudentTestsData?.totaltests}
          handlePageSizeChange={e => {
            const pageSize = e.target?.value;
            setPage(0);
            setSize(+pageSize);
          }}
          currentPage={page}
          handlePageChange={e => setPage(e)}
        />
      )}
    </div>
  );
};

export default AssignedTests;

// -------------------------------
// TEST CARD COMPONENT
// -------------------------------
const TestCard = ({ test }: { test: AssignedTest }) => {
  const [getScheduleTEstPattern] = useStartStudentScheduleTestMutation();
  const [fetchFullTestDetails] = useLazyGetWrittenTestFullDetailsQuery();
  const [fetchTestsForCompany] = useLazyGetTestsForCompanyQuery();
  const [fetchResumeTest] = useLazyResumeTestQuery();
  const [discardTestMutate] = useDiscardTestMutation();
  const [fetchInstructions] = useLazyGetInstructionsForTestQuery();

  const [now, setNow] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [roundInstructions, setRoundInstructions] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [planMessage, setPlanMessage] = useState('');
  const startTime = new Date(`${test.testStartTime}z`);
  const endTime = new Date(`${test.testEndTime}z`);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const startTest = async (
    userId: string,
    scheduledTestId: string,
    test: AssignedTest
  ) => {
    try {
      const response = await getScheduleTEstPattern({
        userId,
        scheduledTestId
      }).unwrap();
      if (response?.status === 'success') {
        const userTestId = response?.data?.id;
        const targetUserId = response?.data?.userId;
        const fullTestResponse = await fetchFullTestDetails({
          userTestId,
          userId: targetUserId
        }).unwrap();
        const fullTestDTO = fullTestResponse?.data;
        const examData: ExamPattern = {
          ...fullTestDTO?.examPatternDTO,
          rounds: fullTestDTO?.examPatternDTO?.rounds?.map((round: Round) => ({
            ...round,
            sections: [...round.sections]?.sort(
              (a, b) => a?.sectionSortOrder - b?.sectionSortOrder
            )
          }))
        };
        dispatch(setHasReadRoundInsructions(true));
        dispatch(
          setCompleteTestData([{ ...response?.data, testDTO: fullTestDTO }])
        );
        dispatch(setNewTemplete([examData]));
        dispatch(setUserSelectedCompanyTest(test));
        dispatch(setUserTestId(response.data.id));
        dispatch(setIsStartTest(true));
        dispatch(
          setCurrentTime(examData?.rounds[0]?.sections[0]?.sectionSpanTime)
        );
        // setIsModalOpen(true);
      }
    } catch (error) {
      console.error(error);
      const err = error as { status?: number; data?: { message?: string } };
      if (err?.status === 402) {
        setPlanMessage(err?.data?.message ?? '');
        setUpgradeDialogOpen(true);
      }
    }
  };

  const resumeTest = async (
    userTestId: string | null,
    testSetId: string | null,
    test: AssignedTest
  ) => {
    if (!userTestId || !testSetId) return;
    const response = await fetchResumeTest({ userTestId, testSetId }).unwrap();
    if (response?.data) {
      const { questions, templete, fulltest, completeTest } = response.data;
      dispatch(setNewTemplete(templete));
      dispatch(setInitailStartFullTestState(fulltest));
      dispatch(setIsStartTest(true));
      dispatch(setInitialQuestionsState(questions));
      dispatch(setCompleteTestData(completeTest));
      dispatch(setUserSelectedCompanyTest(test));
    }
  };

  const discardTest = async (userTestId: string, testSetId: string) => {
    const response = await discardTestMutate({ userTestId, testSetId });
    const companyId = 'a'; // userSelectedCompany?.id;
    const totalTestCount = 'a'; // userSelectedCompany?.testCount;
    if (companyId && totalTestCount) {
      const response = await fetchTestsForCompany({
        companyId,
        totalTestCount
      });
      if (response?.data && response?.isSuccess)
        dispatch(setUserCompanyTestsStatus(response?.data));
    }
  };

  const handleAction = async (action: any, test: AssignedTest) => {
    //setIsLoading(true);
    const { userId, id } = test;
    if (action === 'View Results') {
      //navigate(`/results/${test.userTestId}`);
    }
    try {
      if (action === 'Start Test') {
        startTest(userId, id, test);
      } else if (action === 'Resume Test') {
        // resumeTest(userTestId, testId, test);
      } else if (action === 'Discard') {
        //discardTest(userTestId, testSetId);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getInstructions = async (test: AssignedTest) => {
    dispatch(setUserSelectedCompanyTest(test));
    try {
      const testId = test?.testId;
      const response = await fetchInstructions(testId).unwrap();
      setRoundInstructions(response?.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching instructions:', error);
    }
  };

  const renderActionButtons = (test: AssignedTest) => {
    switch (test?.testStatus) {
      case 'NOT_YET':
        return (
          <button
            onClick={() => getInstructions(test)}
            disabled={isLoading}
            className='flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-alpha transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto'
          >
            <Play className='h-4 w-4' />
            Start Test
          </button>
        );

      case 'STARTED':
        return (
          <div className='flex flex-row gap-2 sm:flex-wrap'>
            <button
              onClick={() => handleAction('Resume Test', test)}
              disabled={isLoading}
              className='flex items-center justify-center gap-2 rounded-lg bg-[#3F826D] px-3 py-2 text-sm font-medium text-alpha transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
            >
              <Play className='h-4 w-4' />
              Resume Test
            </button>
            <button
              onClick={() => handleAction('Discard', test)}
              disabled={isLoading}
              className='flex items-center justify-center gap-2 rounded-lg bg-[#A4031F] px-3 py-2 text-sm font-medium text-alpha transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
            >
              <Trash2 className='h-4 w-4' />
            </button>
          </div>
        );

      case 'COMPLETED':
        return (
          <button
            onClick={() => handleAction('View Results', test)}
            disabled={isLoading}
            className='flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-alpha transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto'
          >
            View Results
          </button>
        );

      default:
        return null;
    }
  };

  // Update time every 1 second
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const isBeforeStart = now < startTime;
  const isLive = now >= startTime && now <= endTime;
  const isCompleted = now > endTime;

  // Format duration
  const formatTime = (ms: number): string => {
    if (ms <= 0) return '00:00:00';

    const sec = Math.floor(ms / 1000);

    const days = Math.floor(sec / (3600 * 24));
    const hours = Math.floor((sec % (3600 * 24)) / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    const seconds = sec % 60;

    const h = String(hours).padStart(2, '0');
    const m = String(minutes).padStart(2, '0');
    const s = String(seconds).padStart(2, '0');

    return days > 0 ? `${days}d ${h}:${m}:${s}` : `${h}:${m}:${s}`;
  };

  const timeUntilStart = startTime.getTime() - now.getTime();

  const DialogContent = () => {
    return (
      <div className='flex flex-col items-center justify-center space-y-4 p-6 text-center'>
        <p className='text-lg font-medium text-gray-800'>
          Please upgrade your plan to continue.
        </p>
        <p className='text-sm text-gray-500'>{planMessage}</p>
        <Button
          handleClickBtn={() => navigate('/payments')}
          label='Explore Plans'
          variant='contained'
        />
      </div>
    );
  };

  return (
    <>
      <div
        className={`flex w-full items-center justify-between rounded-xl border border-primary p-4 shadow-primary ${
          isCompleted
            ? 'pointer-events-none bg-gray-200 opacity-50'
            : 'bg-white'
        }`}
      >
        <h2 className='text-xl font-semibold'>{test.testName}</h2>

        {/* BEFORE START TIME → show countdown */}
        {isBeforeStart && (
          <div className='font-medium text-primary'>
            Test starts in: {formatTime(timeUntilStart)}
          </div>
        )}

        {/* LIVE PERIOD → show START TEST button */}
        {isLive && renderActionButtons(test)}

        {/* AFTER END TIME → show COMPLETED */}
        {isCompleted && (
          <div className='font-semibold text-red-600'>Test Ended</div>
        )}
      </div>
      <CommonDialog
        isOpen={isModalOpen}
        close={() => setIsModalOpen(false)}
        childClasses='px-0 pb-0 pt-0'
        width='w-[90%] md:w-[60%] max-h-[85dvh] md:max-h-[80dvh]'
      >
        <div className='px-6 py-4'>
          <div className='flex flex-col  items-center justify-end p-3'>
            <div
              className='prose prose-sm h-[65dvh] space-y-3 overflow-y-auto font-normal text-blue_a12 md:h-fit'
              dangerouslySetInnerHTML={{ __html: roundInstructions }}
            />
            <div className='mt-1 self-end'>
              <Button
                disabled={false}
                label='Start Test'
                variant='contained'
                handleClickBtn={() => {
                  handleAction('Start Test', test);
                }}
              />
            </div>
          </div>
        </div>
      </CommonDialog>
      <CommonDialog
        isOpen={upgradeDialogOpen}
        close={() => setUpgradeDialogOpen(false)}
      >
        <DialogContent />
      </CommonDialog>
    </>
  );
};
